/**
 * @license
 * Copyright (c) 2026 STACK SOFTWARE. All Rights Reserved.
 * Proprietary and confidential. See LICENSE in the project root.
 */
import {
  AfterViewInit,
  Component,
  OnInit,
  OnDestroy,
  Inject,
} from '@angular/core';
import { TokenService } from './@core/services/token.service';
import { MenuNavigationService } from './@core/services/menu-navigation.service';
import { NzI18nService, en_US } from 'ng-zorro-antd/i18n';
import { NzMessageService } from 'ng-zorro-antd/message';
import {
  MsalService,
  MsalBroadcastService,
  MSAL_GUARD_CONFIG,
  MsalGuardConfiguration,
} from '@azure/msal-angular';
import {
  InteractionStatus,
  EventMessage,
  EventType,
  AuthenticationResult,
} from '@azure/msal-browser';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { Router } from '@angular/router';
import { AuthService } from './@core/authentication/auth.service';

@Component({
    selector: 'ngx-app',
    template: `
    <!-- Loading overlay for Azure SSO -->
    @if (isAuthenticating) {
      <div class="auth-loading-overlay">
        <div class="auth-loading-content">
          <div class="auth-spinner"></div>
          <h3>Signing you in...</h3>
          <p>Please wait while we complete your authentication.</p>
        </div>
      </div>
    }
    <router-outlet></router-outlet>
    `,
    styles: [
        `
      .auth-loading-overlay {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: rgba(255, 255, 255, 0.95);
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 9999;
      }

      .auth-loading-content {
        text-align: center;
        padding: 40px;
      }

      .auth-loading-content h3 {
        margin-top: 20px;
        margin-bottom: 10px;
        color: #333;
        font-size: 1.5rem;
      }

      .auth-loading-content p {
        color: #666;
        font-size: 1rem;
      }

      .auth-spinner {
        width: 50px;
        height: 50px;
        border: 4px solid #f3f3f3;
        border-top: 4px solid #3498db;
        border-radius: 50%;
        animation: spin 1s linear infinite;
        margin: 0 auto;
      }

      @keyframes spin {
        0% {
          transform: rotate(0deg);
        }
        100% {
          transform: rotate(360deg);
        }
      }
    `,
    ],
    standalone: false
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly _destroying$ = new Subject<void>();
  isAuthenticating = false;

  constructor(
    private readonly tokenService: TokenService,
    private readonly i18n: NzI18nService,
    private readonly msalService: MsalService,
    private readonly msalBroadcastService: MsalBroadcastService,
    private readonly router: Router,
    private readonly authService: AuthService,
    private readonly message: NzMessageService,
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
  ) {}

  ngOnInit(): void {
    this.i18n.setLocale(en_US);

    // Check if we're returning from Azure AD redirect (URL contains hash or code)
    const currentUrl = window.location.href;
    const hasAuthParams =
      currentUrl.includes('#code=') ||
      currentUrl.includes('#id_token=') ||
      currentUrl.includes('?code=') ||
      currentUrl.includes('#access_token=') ||
      window.location.hash.includes('id_token') ||
      window.location.hash.includes('access_token');

    if (hasAuthParams) {
      this.isAuthenticating = true;
    }

    // Subscribe to interaction status changes to show/hide loading
    this.msalBroadcastService.inProgress$
      .pipe(takeUntil(this._destroying$))
      .subscribe((status: InteractionStatus) => {
        // Show loading when there's an active interaction (except None and Startup)
        if (
          status === InteractionStatus.HandleRedirect ||
          status === InteractionStatus.Login ||
          status === InteractionStatus.AcquireToken
        ) {
          this.isAuthenticating = true;
        } else if (status === InteractionStatus.None) {
          // Only hide if we're not processing a token
          if (!this.isProcessingToken) {
            this.isAuthenticating = false;
          }
          this.checkAndSetActiveAccount();
        }
      });

    // Handle redirect callback from Azure AD
    this.msalService.handleRedirectObservable().subscribe({
      next: (result: AuthenticationResult | null) => {
        if (result) {
          console.log('MSAL redirect result:', result);
          this.isAuthenticating = true;
          this.isProcessingToken = true;

          // Set the active account
          this.msalService.instance.setActiveAccount(result.account);

          // Process the token with our backend
          if (result.idToken) {
            this.authService.processAzureRedirect(result.idToken).subscribe({
              next: (jwt: any) => {
                const token = jwt?.data?.azureLogin;
                if (!token?.accessToken) {
                  this.failAuthentication(
                    'Unable to sign in.',
                    jwt?.errors ?? jwt,
                  );
                  return;
                }
                this.tokenService.setToken(token);
                this.isAuthenticating = false;
                this.isProcessingToken = false;
                this.router.navigate(['/pages/dashboard']);
              },
              error: (error) => {
                this.failAuthentication(
                  this.extractErrorMessage(error),
                  error,
                );
              },
            });
          } else {
            this.failAuthentication(
              'Sign-in did not return an identity token.',
              result,
            );
          }
        } else {
          // No result means no redirect was processed
          this.isAuthenticating = false;
        }
      },
      error: (error) => {
        this.failAuthentication(this.extractErrorMessage(error), error);
      },
    });

    // Subscribe to MSAL events
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS,
        ),
        takeUntil(this._destroying$),
      )
      .subscribe((result: EventMessage) => {
        console.log('Login success event:', result);
        const payload = result.payload as AuthenticationResult;
        this.msalService.instance.setActiveAccount(payload.account);
      });
  }

  private isProcessingToken = false;

  ngAfterViewInit() {
    this.tokenService.get().subscribe((token) => {
      if (!token) {
        return;
      }
    });
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }

  /**
   * Always clears the "Signing you in..." overlay, tells the user why sign-in
   * failed, and returns them to the login page. Every SSO failure path must go
   * through here — otherwise the overlay stays up forever with no explanation.
   */
  private failAuthentication(reason: string, detail?: unknown): void {
    console.error('Azure SSO sign-in failed:', reason, detail);
    this.isAuthenticating = false;
    this.isProcessingToken = false;
    this.message.error(reason || 'Unable to sign in.', { nzDuration: 8000 });
    this.router.navigate(['/auth/login']);
  }

  private extractErrorMessage(error: any): string {
    return (
      error?.graphQLErrors?.[0]?.message ||
      error?.errors?.[0]?.message ||
      error?.errorMessage ||
      error?.message ||
      'Unable to sign in.'
    );
  }

  private checkAndSetActiveAccount(): void {
    const activeAccount = this.msalService.instance.getActiveAccount();
    if (
      !activeAccount &&
      this.msalService.instance.getAllAccounts().length > 0
    ) {
      const accounts = this.msalService.instance.getAllAccounts();
      this.msalService.instance.setActiveAccount(accounts[0]);
    }
  }
}
